// src/store/loggerSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiUrl } from "../apiUrl";

// Async thunk para enviar logs al servidor
export const sendLog = createAsyncThunk(
  "logger/sendLog",
  async (logData, { rejectWithValue }) => {
    try {
      // En desarrollo, simular éxito para evitar errores de CORS
      if (process.env.NODE_ENV === "development") {
        console.log("📤 Log would be sent to server (simulated):", logData);
        return { success: true, simulated: true };
      }

      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${apiUrl}/api/v1/logs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(logData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error sending log to server:", error);
      return rejectWithValue(error.message);
    }
  }
);

const loggerSlice = createSlice({
  name: "logger",
  initialState: {
    logs: [], // Logs para mostrar en UI
    pendingLogs: [], // Logs pendientes de enviar al servidor
    isSending: false,
    error: null,
  },
  reducers: {
    addLog: (state, action) => {
      const newLog = {
        ...action.payload,
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
      };

      // Agregar log al array local para UI
      state.logs.unshift(newLog);

      // Mantener sólo los últimos 50 logs en memoria
      if (state.logs.length > 50) {
        state.logs.pop();
      }

      // Solo agregar a pendingLogs si es un log que debe ir al servidor
      // y no estamos en desarrollo
      if (process.env.NODE_ENV !== "development") {
        // Crear copia sin los campos que van por separado
        const {
          file,
          line,
          stack,
          component,
          level,
          message,
          ...cleanAdditionalData
        } = newLog;

        const serverLogData = {
          level: newLog.level,
          message: newLog.message,
          url: window.location.href,
          file: newLog.file || "",
          line: newLog.line || "",
          stack_trace: newLog.stack || "",
          additional_data: {
            // ✅ Cambiado: ahora es objeto, no string
            component: newLog.component || "unknown",
            ...cleanAdditionalData,
          },
        };

        state.pendingLogs.push({
          ...serverLogData,
          id: newLog.id, // Mismo ID para poder hacer match después
          originalTimestamp: newLog.timestamp,
        });
      }
    },
    removePendingLog: (state, action) => {
      state.pendingLogs = state.pendingLogs.filter(
        (log) => log.id !== action.payload
      );
    },
    clearPendingLogs: (state) => {
      state.pendingLogs = [];
    },
    clearLogs: (state) => {
      state.logs = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendLog.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendLog.fulfilled, (state, action) => {
        state.isSending = false;

        // Remover el log enviado exitosamente de pendingLogs
        if (action.meta.arg && action.meta.arg.id) {
          state.pendingLogs = state.pendingLogs.filter(
            (log) => log.id !== action.meta.arg.id
          );
        }

        // Si fue simulado (desarrollo), no hacer nada
        if (action.payload?.simulated) {
          return;
        }

        // Agregar confirmación al log en UI
        const logIndex = state.logs.findIndex(
          (log) => log.id === action.meta.arg.id
        );
        if (logIndex !== -1) {
          state.logs[logIndex].sentToServer = true;
          state.logs[logIndex].serverResponse = action.payload;
        }
      })
      .addCase(sendLog.rejected, (state, action) => {
        state.isSending = false;
        state.error = action.payload;

        // Marcar el log como fallido en UI
        if (action.meta.arg && action.meta.arg.id) {
          const logIndex = state.logs.findIndex(
            (log) => log.id === action.meta.arg.id
          );
          if (logIndex !== -1) {
            state.logs[logIndex].sendError = action.payload;
            state.logs[logIndex].retryCount =
              (state.logs[logIndex].retryCount || 0) + 1;
          }
        }

        // Agregar el error al array de logs
        state.logs.unshift({
          level: "error",
          message: `Error enviando log al servidor: ${action.payload}`,
          timestamp: new Date().toISOString(),
          id: Date.now() + Math.random(),
          isSystemLog: true,
        });
      });
  },
});

export const {
  addLog,
  removePendingLog,
  clearPendingLogs,
  clearLogs,
  clearError,
} = loggerSlice.actions;
export default loggerSlice.reducer;
